import { useQuery } from '@tanstack/react-query';
import { Alert, CircularProgress, Divider, Stack, Typography } from '@mui/material';
import { useApplicationFormApi } from './applicationFormApi';
import { StudentListItem } from '../../../types/student';
import { isTemporaryNumber, TEMPORARY_LABEL } from '../../../components/temporaryNumber';

interface Step11Props {
  studentId: number | null;
}

const summaryRow = (label: string, value?: string | number | null) =>
  value !== undefined && value !== null && value !== '' ? (
    <Stack direction="row" justifyContent="space-between" sx={{ py: 0.5 }}>
      <Typography variant="body2" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body2" sx={{ fontWeight: 600, textAlign: 'right' }}>
        {value}
      </Typography>
    </Stack>
  ) : null;

const RECOMMENDED_FIELDS: Array<{ label: string; check: (s: StudentListItem) => boolean }> = [
  { label: 'Email', check: (s) => !!s.email },
  { label: 'Date of birth', check: (s) => !!s.dateOfBirth },
  { label: 'NIC or passport', check: (s) => !!(s.nicNumber || s.passportNumber) },
  { label: 'Nationality', check: (s) => !!s.nationalityCountry },
  { label: 'Preferred university', check: (s) => !!s.preferredUniversity },
  { label: "Father's or mother's name", check: (s) => !!(s.fatherName || s.motherName) },
  { label: 'Declaration accepted', check: (s) => s.studentAgreementAccepted },
];

export const Step11Review = ({ studentId }: Step11Props) => {
  const { getStudent, mode } = useApplicationFormApi();
  const studentQuery = useQuery({
    queryKey: ['student', mode, studentId],
    queryFn: () => getStudent(studentId!),
    enabled: !!studentId,
  });

  if (!studentId) {
    return (
      <Typography variant="body2" color="text.secondary">
        Complete Step 2 first.
      </Typography>
    );
  }
  if (studentQuery.isLoading || !studentQuery.data) return <CircularProgress size={24} />;

  const student = studentQuery.data;
  const missing = RECOMMENDED_FIELDS.filter((f) => !f.check(student));

  return (
    <Stack spacing={2}>
      <Typography variant="body2" color="text.secondary">
        Review the application below before saving.
      </Typography>

      {missing.length > 0 && (
        <Alert severity="warning">
          Missing (optional but recommended): {missing.map((m) => m.label).join(', ')}
        </Alert>
      )}

      <Typography variant="subtitle2">Application</Typography>
      {summaryRow(isTemporaryNumber(student.applicationNumber) ? TEMPORARY_LABEL.application : 'Application number', student.applicationNumber)}
      {summaryRow('Branch', student.branch?.name)}
      {summaryRow('University', student.preferredUniversity?.name)}
      {summaryRow('Faculty', student.preferredFaculty?.name)}
      {summaryRow('Program', student.preferredProgram?.name)}
      {summaryRow('Intake', student.preferredIntake?.name)}

      <Divider />
      <Typography variant="subtitle2">Personal details</Typography>
      {summaryRow('Name', `${student.firstName} ${student.lastName}`)}
      {summaryRow('Preferred name', student.preferredName)}
      {summaryRow('Gender', student.gender)}
      {summaryRow('Date of birth', student.dateOfBirth?.slice(0, 10))}
      {summaryRow('NIC', student.nicNumber)}
      {summaryRow('Passport', student.passportNumber)}
      {summaryRow('Nationality', student.nationalityCountry?.name)}
      {summaryRow('Mobile', student.phone)}
      {summaryRow('Email', student.email)}
      {summaryRow('Address', [student.address, student.city, student.postalCode].filter(Boolean).join(', '))}

      <Divider />
      <Typography variant="subtitle2">Education</Typography>
      {summaryRow('School', student.schoolName)}
      {summaryRow('O/L', [student.olType, student.olYear].filter(Boolean).join(' · '))}
      {summaryRow('A/L', [student.alType, student.alYear, student.alStream].filter(Boolean).join(' · '))}

      <Divider />
      <Typography variant="subtitle2">Preferred study</Typography>
      {summaryRow('Qualification', student.preferredQualificationLevel?.name)}
      {summaryRow('Country', student.country?.name)}
      {summaryRow('Budget', student.budget ? `${student.budgetCurrency?.code ?? ''} ${student.budget}` : undefined)}
      {summaryRow('English test', student.englishTestType)}

      <Divider />
      <Typography variant="subtitle2">Parents &amp; guardian</Typography>
      {summaryRow('Father', [student.fatherName, student.fatherPhone].filter(Boolean).join(' · '))}
      {summaryRow('Mother', [student.motherName, student.motherPhone].filter(Boolean).join(' · '))}
      {summaryRow('Guardian', [student.guardianName, student.guardianPhone].filter(Boolean).join(' · '))}

      <Divider />
      <Typography variant="subtitle2">Visa history</Typography>
      {summaryRow('Applied before', student.visaAppliedBefore ? 'Yes' : 'No')}
      {summaryRow('Refused', student.visaRefused ? 'Yes' : 'No')}

      <Divider />
      <Typography variant="subtitle2">Financial</Typography>
      {summaryRow('Funding method', student.fundingMethod)}
      {summaryRow('Monthly income', student.monthlyIncome)}

      <Divider />
      <Typography variant="subtitle2">Declaration</Typography>
      {summaryRow('Agreement accepted', student.studentAgreementAccepted ? 'Yes' : 'No')}
      {summaryRow('Student signature', student.studentSignatureName)}
      {summaryRow('Guardian signature', student.guardianSignatureName)}
    </Stack>
  );
};
